import {
	Alert,
	AlertIcon,
	Badge,
	Box,
	Flex,
	SimpleGrid,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	Text,
	useColorModeValue
} from '@chakra-ui/react';
import { useEffect } from 'react';
import Card from '../../components/card/Card';
import { Role } from '../../models/user.model';
import { fetchLiveLeadDaily, fetchLiveLeadMe } from '../../store/features/liveLead/liveLead.slice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store/store';
import ClientesAdmin from './components/ClientesAdmin';
import EntregasTable from './components/EntregasTable';
import IntegracaoCard from './components/IntegracaoCard';
import VolumeChart from './components/VolumeChart';

function Metric({ label, value, hint }: { label: string; value: number; hint?: string }) {
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
	return (
		<Card p='20px'>
			<Text fontSize='sm' color={subColor}>
				{label}
			</Text>
			<Text fontSize='2xl' fontWeight='bold' color={textColor}>
				{value.toLocaleString('pt-BR')}
			</Text>
			{hint ? (
				<Text fontSize='xs' color={subColor}>
					{hint}
				</Text>
			) : null}
		</Card>
	);
}

/**
 * Painel do Lead ao Vivo.
 *
 * O cliente vê só a própria conta — volume recebido, integração e histórico.
 * ADMIN e SUPORTE ganham uma aba a mais com a lista de clientes, que é onde a
 * feature é ligada e desligada conta a conta.
 */
export default function LeadAoVivo() {
	const dispatch = useAppDispatch();
	const { config, stats, loading, error } = useAppSelector((state: RootState) => state.liveLead);
	const role = useAppSelector((state: RootState) => state.auth.role);
	const isStaff = role === Role.ADMIN || role === Role.SUPORTE;
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');

	useEffect(() => {
		dispatch(fetchLiveLeadMe());
		dispatch(fetchLiveLeadDaily(14));
	}, [dispatch]);

	return (
		<Box mt={{ base: '130px', md: '80px' }}>
			<Flex align='center' gap={3} mb='20px'>
				<Text fontSize='2xl' fontWeight='bold' color={textColor}>
					Lead ao Vivo
				</Text>
				{config ? (
					<Badge colorScheme={config.enabled ? 'green' : 'gray'} borderRadius='full' px={3}>
						{config.enabled ? 'Ativo' : 'Desligado'}
					</Badge>
				) : null}
			</Flex>

			{error ? (
				<Alert status='error' mb='20px' borderRadius='md'>
					<AlertIcon />
					{error}
				</Alert>
			) : null}

			{config && !config.enabled ? (
				<Alert status='info' mb='20px' borderRadius='md'>
					<AlertIcon />
					A entrega está desligada para esta conta. Fale com o suporte para ativar.
				</Alert>
			) : null}

			{config && config.enabled && !config.webhookUrl ? (
				<Alert status='warning' mb='20px' borderRadius='md'>
					<AlertIcon />
					Nenhum webhook configurado — os leads ficam na fila até você informar uma URL abaixo.
				</Alert>
			) : null}

			<SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} gap='20px' mb='20px'>
				<Metric label='Últimas 24h' value={stats?.sent24h ?? 0} />
				<Metric label='7 dias' value={stats?.sent7d ?? 0} />
				<Metric label='30 dias' value={stats?.sent30d ?? 0} />
				<Metric label='Total' value={stats?.total ?? 0} />
				<Metric
					label='Na fila'
					value={stats?.pending ?? 0}
					hint={config ? `sai ${config.delayMinutes} min após entrar` : undefined}
				/>
				<Metric label='Com falha' value={stats?.failed ?? 0} />
			</SimpleGrid>

			<Tabs variant='soft-rounded' colorScheme='brand'>
				<TabList mb='16px'>
					<Tab>Recebimento</Tab>
					<Tab>Integração</Tab>
					{isStaff ? <Tab>Clientes</Tab> : null}
				</TabList>
				<TabPanels>
					<TabPanel px={0}>
						<VolumeChart />
						<Box mt='20px'>
							<EntregasTable />
						</Box>
					</TabPanel>
					<TabPanel px={0}>
						<IntegracaoCard />
					</TabPanel>
					{isStaff ? (
						<TabPanel px={0}>
							<ClientesAdmin />
						</TabPanel>
					) : null}
				</TabPanels>
			</Tabs>

			{loading ? (
				<Text mt='10px' fontSize='sm' color={subColor}>
					Carregando…
				</Text>
			) : null}
		</Box>
	);
}
